import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { doc, getDoc, addDoc, collection, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { CircularProgress, TextField, MenuItem, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { motion } from 'framer-motion';
import { BsCalendarEvent, BsClock, BsInfoCircle } from 'react-icons/bs';
import { FaTicketAlt, FaMobileAlt, FaLock, FaUser, FaEnvelope } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { processPayment } from '../../utils/paymentService';

const Container = styled.div`
  padding: 2rem;
  max-width: 1000px;
  margin: 0 auto;
`;

const EventCard = styled(motion.div)`
  background: linear-gradient(145deg, #1a1e2e 0%, #0d1017 100%);
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`;

const EventImage = styled.img`
  width: 100%;
  height: 400px;
  object-fit: cover;
`;

const EventContent = styled.div`
  padding: 2rem;
`;

const EventTitle = styled.h1`
  color: #fff;
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
`;

const EventInfo = styled.div`
  display: flex;
  gap: 2rem;
  margin-bottom: 2rem;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #4b7bec;
  font-size: 1rem;

  svg {
    font-size: 1.2rem;
  }
`;

const EventDescription = styled.p`
  color: #a0a0a0;
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const PurchaseSection = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const PriceTag = styled.div`
  font-size: 2rem;
  color: #4b7bec;
  font-weight: 600;
`;

const PurchaseButton = styled.button`
  flex: 1;
  padding: 1rem;
  background: linear-gradient(90deg, #4b7bec, #3867d6);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const ErrorContainer = styled(motion.div)`
  padding: 2rem;
  background: rgba(255, 0, 0, 0.1);
  border-radius: 12px;
  margin: 2rem auto;
  max-width: 600px;
  text-align: center;
  color: #ff6b6b;
  border: 1px solid rgba(255, 0, 0, 0.2);
`;

const PaymentForm = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 2rem;
  margin-top: 2rem;
  border: 1px solid rgba(74, 144, 226, 0.1);
`;

const FormTitle = styled.h3`
  color: white;
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: #4b7bec;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const PaymentMethodSelector = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const PaymentMethod = styled.button`
  background: ${props => props.active ? 'rgba(74, 144, 226, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid ${props => props.active ? '#4b7bec' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 8px;
  padding: 1rem;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(74, 144, 226, 0.1);
  }

  svg {
    color: #4b7bec;
  }
`;

const TicketTypeSelector = styled.div`
  margin-bottom: 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 1rem;
`;

const TicketTypeTitle = styled.h4`
  color: white;
  font-size: 1.2rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: #4b7bec;
  }
`;

const TicketOptions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const TicketOption = styled.div`
  background: ${props => props.selected ? 'rgba(74, 144, 226, 0.2)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.selected ? '#4b7bec' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(74, 144, 226, 0.1);
  }
`;

const TicketOptionTitle = styled.h5`
  color: white;
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
`;

const TicketOptionPrice = styled.div`
  color: #4b7bec;
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const TicketOptionDescription = styled.p`
  color: #a0a0a0;
  font-size: 0.9rem;
`;

const SuccessMessage = styled(motion.div)`
  background: rgba(39, 174, 96, 0.1);
  border: 1px solid rgba(39, 174, 96, 0.2);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  margin-top: 2rem;
  color: #2ecc71;
`;

const ErrorMessage = styled(motion.div)`
  background: rgba(231, 76, 60, 0.1);
  border: 1px solid rgba(231, 76, 60, 0.2);
  border-radius: 12px;
  padding: 1rem;
  text-align: center;
  margin-top: 1rem;
  color: #e74c3c;
  font-size: 0.9rem;
`;

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  
  // Payment form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobileNumber: '',
    mobileProvider: 'mtn',
    ticketType: 'standard' // standard or premium
  });
  
  // Validation errors
  const [validationErrors, setValidationErrors] = useState({
    name: '',
    email: '',
    mobileNumber: ''
  });

  useEffect(() => {
    const fetchEventDetails = async () => {
      try {
        const eventRef = doc(db, 'streamEvents', id);
        const eventDoc = await getDoc(eventRef);
        
        if (!eventDoc.exists()) {
          setError('Event not found');
          return;
        }

        setEvent({
          id: eventDoc.id,
          ...eventDoc.data()
        });
      } catch (error) {
        console.error('Error fetching event details:', error);
        setError('Failed to load event details');
      } finally {
        setLoading(false);
      }
    };

    fetchEventDetails();
  }, [id]);
  
  // Pre-fill form with user data if available
  useEffect(() => {
    if (currentUser && currentUser.email) {
      setFormData(prev => ({
        ...prev,
        email: currentUser.email,
        name: currentUser.displayName || ''
      }));
    }
  }, [currentUser]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation errors when user types
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear payment error when user makes changes
    if (paymentError) {
      setPaymentError('');
    }
  };
  
  const validateMobileNumber = (number) => {
    // Remove any non-digit characters
    const cleaned = number.replace(/\D/g, '');
    
    // Format the number - be more lenient with validation
    let standardized = cleaned;
    
    // If it starts with 0, convert to 256 format
    if (cleaned.startsWith('0')) {
      standardized = '256' + cleaned.substring(1);
    } 
    // If it doesn't start with 256, add it (unless it's already long enough)
    else if (!cleaned.startsWith('256') && cleaned.length <= 9) {
      standardized = '256' + cleaned;
    }
    
    // Be more forgiving with validation - just ensure we have something reasonable
    if (standardized.length < 9) {
      return { valid: false, message: 'Mobile number is too short' };
    }
    
    return { valid: true, formattedNumber: standardized };
  };
  
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    
    // Validate name
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
      isValid = false;
    }
    
    // Validate email
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      isValid = false;
    }
    
    // Validate mobile number
    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile number is required';
      isValid = false;
    } else {
      const validation = validateMobileNumber(formData.mobileNumber);
      if (!validation.valid) {
        newErrors.mobileNumber = validation.message;
        isValid = false;
      }
    }
    
    setValidationErrors(newErrors);
    return isValid;
  };

  const handlePurchase = () => {
    // Check if user is logged in
    if (!currentUser) {
      setShowLoginDialog(true);
      return;
    }
    
    setShowPaymentForm(true);
  };

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress style={{ color: '#4b7bec' }} />
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {error}
      </ErrorContainer>
    );
  }

  // Handle form submission and payment processing
  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    setPaymentError('');
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setPaymentLoading(true);
    
    // Generate a unique order ID that will be used as fallback if Firestore fails
    const fallbackTicketId = `ticket-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
    let ticketRef = null;
    let paymentResult = null;
    
    try {
      // Determine ticket price based on ticket type
      const ticketPrice = formData.ticketType === 'premium' 
        ? event.premiumPrice || (event.price * 2) // Default to 2x standard price if not specified
        : event.price;
      
      // Format mobile number with error handling
      let formattedNumber;
      try {
        const validation = validateMobileNumber(formData.mobileNumber);
        formattedNumber = validation.formattedNumber;
        console.log('Using formatted number:', formattedNumber);
      } catch (error) {
        console.error('Error formatting number:', error);
        // Use a simple fallback approach
        formattedNumber = formData.mobileNumber.replace(/\D/g, '');
        if (formattedNumber.startsWith('0')) {
          formattedNumber = '256' + formattedNumber.substring(1);
        }
        console.log('Using fallback formatted number:', formattedNumber);
      }
      
      // Store ticket details in sessionStorage as backup
      const ticketData = {
        userId: currentUser?.uid || 'guest',
        eventId: event.id,
        eventTitle: event.title,
        ticketType: formData.ticketType,
        price: ticketPrice,
        purchaseDate: new Date().toISOString(),
        status: 'pending',
        contactInfo: {
          name: formData.name,
          email: formData.email,
          mobileNumber: formattedNumber
        },
        fallbackId: fallbackTicketId
      };
      
      // Save to session storage for recovery in case of issues
      sessionStorage.setItem('pendingTicket', JSON.stringify(ticketData));
      
      // Create ticket record in Firestore with timeout protection
      const firestorePromise = addDoc(collection(db, 'tickets'), ticketData);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Firestore operation timed out')), 10000)
      );
      
      try {
        ticketRef = await Promise.race([firestorePromise, timeoutPromise]);
        console.log('Ticket created in Firestore with ID:', ticketRef.id);
      } catch (firestoreError) {
        console.error('Error creating ticket in Firestore:', firestoreError);
        // Continue with payment using fallback ID
        console.log('Using fallback ticket ID:', fallbackTicketId);
      }
      
      // Prepare payment payload
      const paymentPayload = {
        amount: ticketPrice,
        mobileNumber: formattedNumber,
        provider: formData.mobileProvider,
        orderId: ticketRef?.id || fallbackTicketId,
        customerName: formData.name,
        customerEmail: formData.email,
        timestamp: Date.now() // Add timestamp for tracking
      };
      
      console.log('Processing payment with payload:', paymentPayload);
      
      // Use a Promise.race to ensure the payment API call doesn't hang
      const apiTimeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Payment API call timed out after 15 seconds')), 15000)
      );
      
      paymentResult = await Promise.race([
        processPayment(paymentPayload),
        apiTimeoutPromise
      ]);
      
      console.log('Payment result:', paymentResult);
      
      if (paymentResult.success) {
        // Redirect to payment processing page to track status
        const redirectUrl = `/marketplace/payment-processing?reference=${paymentResult.reference || paymentPayload.orderId}${paymentResult.transactionId ? `&transaction_id=${paymentResult.transactionId}` : ''}`;
        
        // Update ticket status in Firestore (non-blocking)
        if (ticketRef) {
          updateDoc(doc(db, 'tickets', ticketRef.id), {
            status: 'processing',
            transactionId: paymentResult.transactionId || '',
            paymentReference: paymentResult.reference || ''
          }).catch(updateError => {
            console.error('Error updating ticket status:', updateError);
          });
        }
        
        setPaymentSuccess(true);
        // Redirect to payment processing page
        setTimeout(() => {
          navigate(redirectUrl);
        }, 1000);
      } else {
        setPaymentError(paymentResult.message || 'Payment processing failed. Please try again.');
        
        // Update ticket status to failed (non-blocking)
        if (ticketRef) {
          updateDoc(doc(db, 'tickets', ticketRef.id), {
            status: 'failed',
            failureReason: paymentResult.message || 'Unknown error'
          }).catch(updateError => {
            console.error('Error updating ticket status:', updateError);
          });
        }
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      setPaymentError(error.message || 'An unexpected error occurred. Please try again later.');
      
      // Log detailed error information for debugging
      console.error('Error stack trace:', error.stack);
      console.error('Payment payload:', {
        ticketId: ticketRef?.id || fallbackTicketId,
        amount: formData.ticketType === 'premium' ? (event.premiumPrice || event.price * 2) : event.price,
        provider: formData.mobileProvider
      });
    } finally {
      setPaymentLoading(false);
    }
  };
  
  const handleLoginRedirect = () => {
    // Save current page URL to redirect back after login
    localStorage.setItem('konzo_redirect_after_login', `/streaming-events/${id}`);
    navigate('/marketplace/login');
  };
  
  return (
    <Container>
      <EventCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <EventImage src={event.image} alt={event.title} />
        <EventContent>
          <EventTitle>{event.title}</EventTitle>
          <EventInfo>
            <InfoItem>
              <BsCalendarEvent />
              <span>{new Date(event.date).toLocaleDateString()}</span>
            </InfoItem>
            <InfoItem>
              <BsClock />
              <span>{event.time}</span>
            </InfoItem>
            <InfoItem>
              <FaTicketAlt />
              <span>${event.price}</span>
            </InfoItem>
          </EventInfo>
          <EventDescription>{event.description}</EventDescription>
          
          {paymentSuccess ? (
            <SuccessMessage
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <h3>Payment Successful!</h3>
              <p>Your ticket has been purchased successfully. You will be redirected to your tickets page shortly.</p>
            </SuccessMessage>
          ) : (
            <PurchaseSection>
              <PriceTag>${event.price}</PriceTag>
              <PurchaseButton onClick={handlePurchase}>
                Purchase Access
              </PurchaseButton>
            </PurchaseSection>
          )}
          
          {showPaymentForm && !paymentSuccess && (
            <PaymentForm
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <FormTitle>
                <FaLock /> Secure Payment
              </FormTitle>
              
              <TicketTypeSelector>
                <TicketTypeTitle>
                  <FaTicketAlt /> Select Ticket Type
                </TicketTypeTitle>
                <TicketOptions>
                  <TicketOption 
                    selected={formData.ticketType === 'standard'}
                    onClick={() => setFormData({...formData, ticketType: 'standard'})}
                  >
                    <TicketOptionTitle>Standard Access</TicketOptionTitle>
                    <TicketOptionPrice>${event.price}</TicketOptionPrice>
                    <TicketOptionDescription>Basic streaming access to the event</TicketOptionDescription>
                  </TicketOption>
                  
                  <TicketOption 
                    selected={formData.ticketType === 'premium'}
                    onClick={() => setFormData({...formData, ticketType: 'premium'})}
                  >
                    <TicketOptionTitle>Premium Access</TicketOptionTitle>
                    <TicketOptionPrice>${event.premiumPrice || (event.price * 2)}</TicketOptionPrice>
                    <TicketOptionDescription>HD streaming + exclusive content</TicketOptionDescription>
                  </TicketOption>
                </TicketOptions>
              </TicketTypeSelector>
              
              <form onSubmit={handleSubmitPayment}>
                <FormTitle>
                  <FaUser /> Contact Information
                </FormTitle>
                
                <FormGrid>
                  <FormGroup>
                    <TextField
                      fullWidth
                      label="Full Name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      variant="outlined"
                      required
                      error={!!validationErrors.name}
                      helperText={validationErrors.name}
                      InputLabelProps={{ style: { color: '#a0a0a0' } }}
                      InputProps={{ style: { color: 'white' } }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                          '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                          '&.Mui-focused fieldset': { borderColor: '#4b7bec' },
                          backgroundColor: 'rgba(255, 255, 255, 0.05)'
                        }
                      }}
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      variant="outlined"
                      required
                      error={!!validationErrors.email}
                      helperText={validationErrors.email}
                      InputLabelProps={{ style: { color: '#a0a0a0' } }}
                      InputProps={{ style: { color: 'white' } }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                          '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                          '&.Mui-focused fieldset': { borderColor: '#4b7bec' },
                          backgroundColor: 'rgba(255, 255, 255, 0.05)'
                        }
                      }}
                    />
                  </FormGroup>
                </FormGrid>
                
                <FormTitle>
                  <FaMobileAlt /> Payment Method
                </FormTitle>
                
                <PaymentMethodSelector>
                  <PaymentMethod 
                    type="button"
                    active={formData.mobileProvider === 'mtn'}
                    onClick={() => setFormData({...formData, mobileProvider: 'mtn'})}
                  >
                    MTN Mobile Money
                  </PaymentMethod>
                  
                  <PaymentMethod 
                    type="button"
                    active={formData.mobileProvider === 'airtel'}
                    onClick={() => setFormData({...formData, mobileProvider: 'airtel'})}
                  >
                    Airtel Money
                  </PaymentMethod>
                </PaymentMethodSelector>
                
                <FormGroup>
                  <TextField
                    fullWidth
                    label="Mobile Number"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleInputChange}
                    variant="outlined"
                    required
                    placeholder="e.g., 0700000000"
                    error={!!validationErrors.mobileNumber}
                    helperText={validationErrors.mobileNumber}
                    InputLabelProps={{ style: { color: '#a0a0a0' } }}
                    InputProps={{ style: { color: 'white' } }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                        '&.Mui-focused fieldset': { borderColor: '#4b7bec' },
                        backgroundColor: 'rgba(255, 255, 255, 0.05)'
                      }
                    }}
                  />
                </FormGroup>
                
                {paymentError && (
                  <ErrorMessage
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {paymentError}
                  </ErrorMessage>
                )}
                
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={paymentLoading}
                  sx={{
                    marginTop: '1.5rem',
                    padding: '0.8rem',
                    backgroundColor: '#4b7bec',
                    '&:hover': { backgroundColor: '#3867d6' },
                    fontWeight: 600
                  }}
                >
                  {paymentLoading ? 'Processing...' : `Pay $${formData.ticketType === 'premium' ? (event.premiumPrice || (event.price * 2)) : event.price}`}
                </Button>
              </form>
            </PaymentForm>
          )}
        </EventContent>
      </EventCard>
      
      {/* Login Dialog */}
      <Dialog 
        open={showLoginDialog} 
        onClose={() => setShowLoginDialog(false)}
        PaperProps={{
          style: {
            backgroundColor: '#1a1e2e',
            color: 'white',
            borderRadius: '12px',
            padding: '1rem'
          }
        }}
      >
        <DialogTitle sx={{ color: 'white' }}>Login Required</DialogTitle>
        <DialogContent>
          <p>You need to be logged in to purchase tickets for this event.</p>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowLoginDialog(false)}
            sx={{ color: '#a0a0a0' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleLoginRedirect}
            variant="contained"
            sx={{ backgroundColor: '#4b7bec', '&:hover': { backgroundColor: '#3867d6' } }}
          >
            Login
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EventDetails;
